// src/screens/traceability/TraceabilityScreen.jsx
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { useRef, useState } from "react";
import EmptyModal from "../../components/modals/EmptyModal";
import SelectMenu from "../../components/controls/SelectMenu";
import ProgressModal from "../../components/modals/ProgressModal";
import { TraceabilityViewModel } from "../../viewmodels/traceability/TraceabilityViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import ManufacturedProductTraceability from "./ManufacturedProductTraceability";
import SupplierItemTraceability from "./SupplierItemTraceability";

const reportTypes = [
  { value: 1, label: "Manufactured product" },
  { value: 2, label: "Supplier item" },
];

// -------------------- Screen --------------------
function TraceabilityScreen(props) {
  const { logout } = props;

  const {
    doLookup,
    loading,
    emptyModal,
    setEmptyModelOpen,
    lookup,
    setLookup,
    err,
    currentWip,
    setSelectedPlu,
    loadPluDetails,
    loadDirectParents,
    selectedPlu,
    incomingPlus,
    byParent,
    byLot,
    getSalesReport,
    // Supplier item props (Trace Children)
    lotNumber,
    source,
    directChildren,
    loadDirectChildren,
    // Legacy receiving for backward compatibility
    receiving,
  } = TraceabilityViewModel(logout);

  const searchRef = useRef();
  const [reportType, setReportType] = useState(reportTypes[0]);

  // Search
  const onSearch = async () => {
    const val = (searchRef.current?.value || "").trim();
    if (!val) return;
    setSelectedPlu(null);
    setLookup(val);
    await doLookup(val, reportType.value);
  };

  return (
    <div className="mt-2 lg:px-8 space-y-4">
      {/* Header / search */}
      <div className="sm:flex sm:items-center px-4 py-5 card-custom">
        <div className="sm:flex-auto w-full">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Traceability
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="relative flex items-stretch flex-grow min-w-[280px]">
              <input
                type="search"
                className="block w-full rounded-l-md pl-2 sm:text-sm border border-gray-300"
                placeholder={
                  reportType?.value === 1
                    ? "Search by shipping barcode, lot or serial number"
                    : "Search by receiving lot number, invoice number, or barcode"
                }
                ref={searchRef}
                defaultValue={lookup}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
              />
              <button
                type="button"
                className="-ml-px inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                onClick={onSearch}
                disabled={loading}
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <SelectMenu
              handleInputChange={(e) => {
                setReportType(e.target.value);
                // Reset state when switching report types
                setLookup("");
                searchRef.current.value = "";
                setSelectedPlu(null);
              }}
              name="report_type"
              options={reportTypes}
              parentClass="rounded-md w-56"
              value={reportType}
            />

            <button
              className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              onClick={() => {
                if (!lookup) return;
                const endpoint =
                  reportType?.value === 1
                    ? `/api/trace/export?lookup=${encodeURIComponent(lookup)}`
                    : `/api/trace/children/v2/export?lookup=${encodeURIComponent(
                        lookup
                      )}`;
                window.open(endpoint, "_blank");
              }}
              disabled={!lookup}
            >
              Export
            </button>
          </div>

          {err ? <p className="mt-3 text-sm text-red-600">{err}</p> : null}
        </div>
      </div>

      {/* Conditionally render based on report type */}
      {reportType?.value === 1 ? (
        <ManufacturedProductTraceability
          currentWip={currentWip}
          incomingPlus={incomingPlus}
          selectedPlu={selectedPlu}
          setSelectedPlu={setSelectedPlu}
          loadPluDetails={loadPluDetails}
          loadDirectParents={loadDirectParents}
          byParent={byParent}
          byLot={byLot}
          getSalesReport={getSalesReport}
        />
      ) : reportType?.value === 2 ? (
        <SupplierItemTraceability
          lookup={lookup}
          loading={loading}
          error={err}
          lotNumber={lotNumber}
          source={source}
          directChildren={directChildren}
          loadDirectChildren={loadDirectChildren}
        />
      ) : null}

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
        size={emptyModal.size}
      />

      <EmptyModal
        childComponent={
          <ProgressModal message="Loading traceability data..." />
        }
        open={loading}
        setOpen={() => {}}
        size="sm:max-w-sm"
      />
    </div>
  );
}

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TraceabilityScreen)
);
